import { documents, type entities } from "@fraction/shared";

/** Full names of a file's types beyond its primary, for `AdditionalDocumentTypesChip`. */
export const additionalTypeFullNames = (file: entities.UploadedFile | null | undefined) =>
  documents
    .fileDocumentTypes(file)
    .slice(file?.type ? 1 : 0)
    .map((type) => type.fullName);

/** Every type the document is, as one comma-separated label — primary first. */
export const fileTypeFullNames = (file: entities.UploadedFile | null | undefined) =>
  documents
    .fileDocumentTypes(file)
    .map((type) => type.fullName)
    .filter(Boolean)
    .join(", ") || undefined;

/** A filename is supporting detail, unless it is the upload's generic placeholder. */
export const documentOriginalFilename = (
  name: string | null | undefined,
  typeFullName?: string | null,
  primaryName?: string
): string | undefined => {
  const filename = name?.trim();
  return filename &&
    filename !== "Generic unknown document type.pdf" &&
    filename !== `${typeFullName}.pdf` &&
    filename !== primaryName
    ? filename
    : undefined;
};

const preferredTypeName = (
  typeName: string | null | undefined,
  typeFullName: string | null | undefined,
  guessedTypeName: string | null | undefined,
  guessedTypeFullName: string | null | undefined,
  originalFilename: string | undefined,
  fallback: string
): string => {
  if (typeFullName && !documents.isPlaceholderDocumentType(typeName)) return typeFullName;
  if (guessedTypeFullName && !documents.isPlaceholderDocumentType(guessedTypeName)) {
    return guessedTypeFullName;
  }
  return originalFilename || typeFullName || guessedTypeFullName || fallback;
};

/** A classified document's type is its primary UI name. */
export const uploadedDocumentDisplayName = (
  file: entities.UploadedFile | null | undefined,
  fallback = "Document"
): string =>
  preferredTypeName(
    file?.type?.name,
    file?.type?.fullName,
    file?.robotGuessedType?.name,
    file?.robotGuessedType?.fullName,
    documentOriginalFilename(file?.s3Document?.name, file?.type?.fullName),
    fallback
  );

/** The same rule for the lightweight document shape returned with checklist items. */
export const checklistDocumentDisplayName = (
  file: {
    typeName?: string | null;
    typeFullName?: string | null;
    robotGuessedDocumentName?: string | null;
    robotGuessedDocumentFullName?: string | null;
    uploadedFileName?: string | null;
  },
  fallback = "Document"
): string =>
  preferredTypeName(
    file.typeName,
    file.typeFullName,
    file.robotGuessedDocumentName,
    file.robotGuessedDocumentFullName,
    documentOriginalFilename(file.uploadedFileName, file.typeFullName),
    fallback
  );
